import React, { useState, useEffect } from 'react';

export default function ProductDetailsView({ locationId, locationName }) {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (locationId) {
      fetchProducts();
    }
  }, [locationId]);

  async function fetchProducts() {
    try {
      setLoading(true);
      const response = await fetch(`/api/products/all-with-thumbnails?locationId=${locationId}`);
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();
      setProducts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function selectProduct(product) {
    try {
      const response = await fetch(`/api/products/${product.guid}`);
      if (!response.ok) throw new Error('Failed to fetch product details');
      const details = await response.json();
      setSelectedProduct(details);
    } catch (err) {
      console.error('Error:', err);
    }
  }

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.menu_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="product-view-container">
      <div className="product-list-panel">
        <h2>📦 Products</h2>
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />

        {loading && <p className="loading">Loading products...</p>}
        {error && <div className="error">✕ {error}</div>}

        <div className="product-list">
          {filteredProducts.map(product => (
            <div
              key={product.guid}
              className={`product-list-item ${selectedProduct?.product?.guid === product.guid ? 'active' : ''}`}
              onClick={() => selectProduct(product)}
            >
              {product.image_url && (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="list-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <div className="list-content">
                <h4>{product.name}</h4>
                <p className="breadcrumb">{product.menu_name} / {product.menu_group_name}</p>
                <p className="price">${(product.price || 0).toFixed(2)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedProduct && (
        <ProductFullDetails product={selectedProduct} />
      )}
    </div>
  );
}

function ProductFullDetails({ product }) {
  const { product: p, images, pricingRules, taxes, allergens, modifierGroups } = product;

  return (
    <div className="product-details-view">
      <div className="details-header">
        <h1>{p.name}</h1>
        {p.pos_name && <p className="pos-name">{p.pos_name}</p>}
      </div>

      {/* Image Gallery */}
      {images && images.length > 0 && (
        <section className="details-section image-section">
          <h3>🖼️ Images ({images.length})</h3>
          <div className="image-gallery">
            {images.map((img, idx) => (
              <div key={idx} className="gallery-item">
                <img
                  src={img.image_url}
                  alt={`Image ${idx + 1}`}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/200?text=Image+Error';
                  }}
                />
                <p className="image-type">{img.image_type}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Basic Info */}
      <section className="details-section">
        <h3>ℹ️ Basic Information</h3>
        <div className="info-grid">
          <InfoItem label="Description" value={p.description || 'N/A'} />
          <InfoItem label="SKU" value={p.sku || 'N/A'} />
          <InfoItem label="PLU" value={p.plu || 'N/A'} />
          <InfoItem label="Calories" value={p.calories || 'N/A'} />
          <InfoItem label="Unit of Measure" value={p.unit_of_measure || 'N/A'} />
          <InfoItem label="Sales Category" value={p.sales_category_name || 'N/A'} />
          <InfoItem label="Kitchen Name" value={p.kitchen_name || 'N/A'} />
          <InfoItem label="Prep Time" value={p.prep_time ? `${p.prep_time} min` : 'N/A'} />
        </div>
      </section>

      {/* Pricing */}
      <section className="details-section">
        <h3>💰 Pricing</h3>
        <div className="pricing-info">
          <div className="price-item">
            <span className="label">Base Price:</span>
            <span className="value">${(p.base_price || 0).toFixed(2)}</span>
          </div>
          <div className="price-item">
            <span className="label">Current Price:</span>
            <span className="value">${(p.price || 0).toFixed(2)}</span>
          </div>
          <div className="price-item">
            <span className="label">Pricing Strategy:</span>
            <span className="value">{p.pricing_strategy || 'N/A'}</span>
          </div>
          <div className="price-item">
            <span className="label">Tax Inclusion:</span>
            <span className="value">{p.tax_inclusion || 'N/A'}</span>
          </div>
        </div>

        {pricingRules && pricingRules.length > 0 && (
          <div className="pricing-rules-section">
            <h4>Time-Specific Pricing Rules</h4>
            <div className="rules-list">
              {pricingRules.map((rule, idx) => (
                <div key={idx} className="rule-card">
                  <div className="rule-row">
                    <span className="rule-label">Base Price:</span>
                    <span className="rule-value">${rule.base_price}</span>
                  </div>
                  <div className="rule-row">
                    <span className="rule-label">Peak Price:</span>
                    <span className="rule-value">${rule.time_specific_price}</span>
                  </div>
                  <div className="rule-row">
                    <span className="rule-label">Days:</span>
                    <span className="rule-value">{JSON.parse(rule.schedule_days || '[]').join(', ')}</span>
                  </div>
                  <div className="rule-row">
                    <span className="rule-label">Time:</span>
                    <span className="rule-value">{rule.time_range_start} - {rule.time_range_end}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Taxes */}
      {taxes && taxes.length > 0 && (
        <section className="details-section">
          <h3>🧾 Tax Information</h3>
          <div className="taxes-table">
            <table>
              <thead>
                <tr>
                  <th>Tax Name</th>
                  <th>Tax ID</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {taxes.map((tax, idx) => (
                  <tr key={idx}>
                    <td>{tax.tax_name || 'Unknown'}</td>
                    <td className="guid">{tax.tax_guid.substring(0, 8)}...</td>
                    <td>{tax.tax_percentage ? `${tax.tax_percentage}%` : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Allergens */}
      {allergens && allergens.length > 0 && (
        <section className="details-section">
          <h3>⚠️ Allergen Information</h3>
          <div className="allergens-list">
            {allergens.map((allergen, idx) => (
              <span key={idx} className="allergen-tag">{allergen}</span>
            ))}
          </div>
        </section>
      )}

      {/* Modifiers */}
      {modifierGroups && modifierGroups.length > 0 && (
        <section className="details-section">
          <h3>🔧 Modifiers & Options</h3>
          <div className="modifiers-section">
            {modifierGroups.map((group, idx) => (
              <div key={idx} className="modifier-group-card">
                <h4>{group.name}</h4>
                <div className="modifier-rules">
                  <span>Select {group.min_selected || 0} to {group.max_selected || 1}</span>
                </div>
                <div className="modifiers-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Modifier Name</th>
                        <th>Price Adjustment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.modifiers && group.modifiers.map((mod, midx) => (
                        <tr key={midx}>
                          <td>{mod.name}</td>
                          <td className="price">
                            {mod.price_adjustment ? (
                              `${mod.price_adjustment > 0 ? '+' : ''}$${mod.price_adjustment.toFixed(2)}`
                            ) : (
                              'No charge'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* POS & Attributes */}
      <section className="details-section">
        <h3>🖨️ POS & Display Attributes</h3>
        <div className="info-grid">
          <InfoItem label="Discountable" value={p.is_discountable ? 'Yes' : 'No'} />
          <InfoItem label="Deferred" value={p.is_deferred ? 'Yes' : 'No'} />
          <InfoItem label="Combo Parent" value={p.is_combo_parent ? 'Yes' : 'No'} />
          <InfoItem label="Contains Alcohol" value={p.contains_alcohol ? 'Yes' : 'No'} />
          <InfoItem label="Button Color (Light)" value={
            <div
              style={{
                display: 'inline-block',
                width: '30px',
                height: '30px',
                backgroundColor: p.pos_button_color_light,
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
              title={p.pos_button_color_light}
            />
          } />
          <InfoItem label="Button Color (Dark)" value={
            <div
              style={{
                display: 'inline-block',
                width: '30px',
                height: '30px',
                backgroundColor: p.pos_button_color_dark,
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
              title={p.pos_button_color_dark}
            />
          } />
        </div>
      </section>

      {/* Dimensions */}
      {(p.weight || p.length || p.width || p.height) && (
        <section className="details-section">
          <h3>📏 Dimensions & Weight</h3>
          <div className="info-grid">
            <InfoItem label="Weight" value={p.weight ? `${p.weight} ${p.weight_unit}` : 'N/A'} />
            <InfoItem label="Length" value={p.length ? `${p.length} ${p.dimension_unit}` : 'N/A'} />
            <InfoItem label="Width" value={p.width ? `${p.width} ${p.dimension_unit}` : 'N/A'} />
            <InfoItem label="Height" value={p.height ? `${p.height} ${p.dimension_unit}` : 'N/A'} />
            <InfoItem label="Guest Count" value={p.guest_count || 'N/A'} />
          </div>
        </section>
      )}

      {/* System Info */}
      <section className="details-section">
        <h3>🔧 System Information</h3>
        <div className="info-grid">
          <InfoItem label="Product GUID" value={<code>{p.guid.substring(0, 16)}...</code>} />
          <InfoItem label="Master ID" value={p.master_id || 'N/A'} />
          <InfoItem label="Multi-Location ID" value={p.multi_location_id || 'N/A'} />
          <InfoItem label="Synced At" value={new Date(p.synced_at).toLocaleString()} />
        </div>
      </section>
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div className="info-item">
      <label>{label}:</label>
      <value-display>{value}</value-display>
    </div>
  );
}
